/** Scroll containers inside the animated area; their scrollbar is hidden while it grows. */
export const heightScrollClass = "height-animator-scroll";
const resizeDuration = 220;

/**
 * Animates the tab area between natural heights whenever its content resizes: a condition collapses,
 * the tab changes or data loads. Between animations the area keeps its automatic height.
 */
export const createHeightAnimator = () => {
	let area: HTMLDivElement | null = null, observer: ResizeObserver | null = null, last: number | null = null, timer = 0, followUntil = 0;
	const heights = new WeakMap<Element, number>();
	// Where the browser animates <details> itself, the area follows that animation instead of running its own.
	const nativeDetails = typeof CSS !== "undefined" && CSS.supports("interpolate-size", "allow-keywords") && CSS.supports("selector(::details-content)");
	const settle = () => {
		window.clearTimeout(timer);
		if (!area) return;
		area.style.removeProperty("height");
		area.style.removeProperty("transition");
		delete area.dataset.resizing;
		delete area.dataset.clip;
	};
	// The area is not observed itself: resizing it inside the observer callback would loop.
	const remember = () => { if (area && area.dataset.resizing === undefined) last = area.getBoundingClientRect().height; };
	const resize = () => {
		if (!area) return;
		// Restart from the height on screen, including one taken mid-animation.
		const from = area.dataset.resizing !== undefined ? area.getBoundingClientRect().height : last;
		settle();
		const to = area.getBoundingClientRect().height;
		last = to;
		if (from === null || Math.abs(to - from) < 1 || performance.now() < followUntil || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		// Hide a scrollbar that would only flash during growth; one that stays keeps the content width unchanged.
		const scrolls = Array.from(area.querySelectorAll<HTMLElement>(`.${heightScrollClass}`)).some(element => element.scrollHeight > element.clientHeight + 1);
		area.dataset.resizing = "";
		if (!scrolls) area.dataset.clip = "";
		area.style.height = `${from}px`;
		void area.offsetHeight;
		area.style.transition = `height ${resizeDuration}ms ease`;
		area.style.height = `${to}px`;
		timer = window.setTimeout(settle, resizeDuration + 50);
	};
	const onResize = (entries: ResizeObserverEntry[]) => {
		let changed = false;
		for (const { target, contentRect } of entries) {
			if (!target.isConnected) { observer?.unobserve(target); continue; }
			if (heights.get(target) === contentRect.height) continue;
			heights.set(target, contentRect.height);
			changed = true;
		}
		if (changed) resize();
	};
	const follow = (event: Event) => {
		if (nativeDetails && event.target instanceof Element && event.target.closest("summary")) followUntil = performance.now() + resizeDuration * 2;
	};
	return {
		area: (node: HTMLDivElement | null) => {
			area?.removeEventListener("click", follow, true);
			if (!node) { settle(); observer?.disconnect(); observer = null; window.removeEventListener("resize", remember); }
			area = node;
			if (!node) return;
			node.addEventListener("click", follow, true);
			window.addEventListener("resize", remember);
		},
		content: (node: HTMLDivElement | null) => {
			if (!node || typeof ResizeObserver === "undefined") return;
			observer ??= new ResizeObserver(onResize);
			observer.observe(node);
		},
	};
};
